import { useState } from 'react';
import BakesPage from './BakesPage';
import { bakes } from './bakes';
import { SiteFooter, SiteHeader } from './SiteChrome';
import './App.css';

function App() {
  const [status, setStatus] = useState('');
  const requestedBake = new URLSearchParams(window.location.search).get('bake');

  async function handleSubmit(event) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const message = [
      'Hi Something Sweet! I would love to ask about an order.',
      '',
      `I'm ${formData.get('name')}.`,
      `I'm interested in: ${formData.get('bake')}.`,
      `The occasion is: ${formData.get('occasion') || 'not specified yet'}.`,
      `The date I have in mind is: ${formData.get('date') || 'not specified yet'}.`,
      `A few more details: ${formData.get('details') || 'I would love to chat about the details.'}`,
      `You can reach me at: ${formData.get('email')}.`,
    ].join('\n');

    try {
      await navigator.clipboard.writeText(message);
      setStatus('Your note is copied. Paste it into your preferred message app to send it.');
    } catch {
      setStatus('We could not access your clipboard. Please try again from a secure browser connection.');
    }
  }

  if (window.location.pathname.replace(/\/+$/, '') === '/bakes') {
    return <BakesPage />;
  }

  return (
    <div className="site-shell">
      <SiteHeader />

      <main id="top">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero__copy">
            <p className="eyebrow"><span className="eyebrow__dot" /> Small-batch bakes, big-day magic</p>
            <h1 id="hero-title">A little joy,<br /><em>baked</em> from scratch.</h1>
            <p className="hero__intro">Thoughtful cakes and nostalgic treats, made by hand for the people and moments you love.</p>
            <div className="hero__actions">
              <a className="button button--dark" href="/bakes">Find your sweet thing <span aria-hidden="true">↘</span></a>
              <a className="text-link" href="#our-story">Get to know us</a>
            </div>
            <div className="hero__note"><span aria-hidden="true">✳</span> Made in a home kitchen<br />with a whole lot of heart</div>
          </div>
          <div className="hero__art">
            <div className="hero__image-wrap">
              <img
                className="hero__image"
                src="https://images.unsplash.com/photo-1565958011703-44f9829ba187?auto=format&fit=crop&w=1200&q=90"
                alt="A homemade strawberry cake with fresh berries"
              />
            </div>
            <div className="hero-stamp" aria-label="Baked with love, always">
              <span>BAKED WITH LOVE</span>
              <span className="hero-stamp__heart" aria-hidden="true">♥</span>
              <span>ALWAYS, ALWAYS</span>
            </div>
            <p className="hero__caption">The good stuff is always homemade.</p>
          </div>
          <span className="hero__side-note" aria-hidden="true">A SWEET LITTLE THING</span>
        </section>

        <section className="favorites section-wrap" id="bakes" aria-labelledby="bakes-title">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Straight from our kitchen</p>
              <h2 id="bakes-title">A few <em>favorites.</em></h2>
            </div>
            <p className="section-heading__aside">The familiar ones, the celebratory ones, and the ones you think about on the drive home.</p>
          </div>
          <div className="bake-grid">
            {bakes.filter((bake) => bake.featured).map((bake, index) => (
              <article className="bake-card" key={bake.name}>
                <a className={`bake-card__image ${bake.className || ''}`} href={`/?bake=${encodeURIComponent(bake.name)}#order`} aria-label={`Ask us about ${bake.name}`}>
                  <img src={bake.image} alt={bake.alt} loading="lazy" />
                  <span className="bake-card__number">0{index + 1}</span>
                  <span className="bake-card__arrow" aria-hidden="true">↗</span>
                </a>
                <div className="bake-card__text">
                  <h3>{bake.name}</h3>
                  <p>{bake.description}</p>
                </div>
              </article>
            ))}
          </div>
          <p className="menu-note">Every bake is made to order. Tell us what you have in mind and we can make it yours. <a href="/bakes">Explore every bake ↗</a></p>
        </section>

        <section className="story" id="our-story" aria-labelledby="story-title">
          <div className="story__image">
            <img
              src="https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=1100&q=85"
              alt="A sunlit home kitchen ready for a day of baking"
              loading="lazy"
            />
            <span className="story__image-label">A home kitchen, always busy</span>
          </div>
          <div className="story__copy">
            <p className="eyebrow">A note from the baker</p>
            <h2 id="story-title">The best things<br />come <em>from home.</em></h2>
            <p>Something Sweet began with a well-loved mixing bowl, a family recipe box, and the belief that a homemade treat can make an ordinary day feel like an occasion.</p>
            <p>These days, every cake is still mixed, baked, and finished by hand. No shortcuts. Just good ingredients, happy accidents, and plenty to share.</p>
            <a className="text-link text-link--dark" href="#order">Tell us what you're celebrating <span aria-hidden="true">↗</span></a>
          </div>
          <span className="story__sparkle" aria-hidden="true">✳</span>
        </section>

        <section className="order section-wrap" id="order" aria-labelledby="order-title">
          <div className="order__intro">
            <p className="eyebrow">Something to celebrate?</p>
            <h2 id="order-title">Let's make<br /><em>it sweet.</em></h2>
            <p>Birthdays, just-because days, and everything worth gathering for. Share a few details and we will help you dream up the right bake.</p>
            <p className="order__small-note"><span aria-hidden="true">✳</span> Every enquiry is a conversation, not a commitment.</p>
          </div>
          <form className="order-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <label className="form-field">
                <span>Your name</span>
                <input type="text" name="name" placeholder="The name we should call you" required />
              </label>
              <label className="form-field">
                <span>Your email</span>
                <input type="email" name="email" placeholder="you@example.com" required />
              </label>
            </div>
            <label className="form-field">
              <span>What sounds good?</span>
              <select name="bake" defaultValue={requestedBake || 'A little of everything'}>
                {requestedBake && !['A little of everything', 'A celebration cake', 'Cookies or a treat box', 'A loaf cake', 'Something custom'].includes(requestedBake) && (
                  <option value={requestedBake}>{requestedBake}</option>
                )}
                <option>A little of everything</option>
                <option>A celebration cake</option>
                <option>Cookies or a treat box</option>
                <option>A loaf cake</option>
                <option>Something custom</option>
              </select>
            </label>
            <div className="form-row">
              <label className="form-field">
                <span>What's the occasion?</span>
                <input type="text" name="occasion" placeholder="Birthday, thank-you, just because..." />
              </label>
              <label className="form-field">
                <span>When do you need it?</span>
                <input type="date" name="date" />
              </label>
            </div>
            <label className="form-field">
              <span>A few more details <span className="form-field__optional">(optional)</span></span>
              <textarea name="details" rows="3" placeholder="Flavours, colours, number of people, or anything else on your mind..." />
            </label>
            <button className="button button--dark order-form__submit" type="submit">Copy your enquiry <span aria-hidden="true">↗</span></button>
            <p className="form-hint" role="status" aria-live="polite">{status || 'This creates a message for you to send; it is not sent automatically.'}</p>
          </form>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

export default App;
